import { track } from "./umami";
import React, { useState, useRef } from "react";
import { ArrowRight, BadgeCheck, Building2, Check, CheckCircle2, Mail, ShieldCheck } from "lucide-react";
import { submitContactSales, type ContactSalesPayload } from "./bridge";
import { siteConfig } from "./config";
import { contactSalesPlan, planById } from "./plans";

type Navigate = (href: string) => (event: React.MouseEvent<HTMLAnchorElement>) => void;

// Option lists mirror the backend validator (handlers/contact_sales.py).
const COMPANY_SIZE = ["1–25", "26–100", "101–500", "501–1,000", "1,000+"];
const USERS = ["1–10", "11–25", "26–100", "100+"];
const JOBS = ["1–25", "26–100", "101–250", "250+"];
const SYSTEMS = ["Local folders", "SMB/network shares", "FTP/SFTP", "AWS S3", "Azure Blob", "Other"];
const SECURITY = ["SSO/LDAP", "IP allowlist", "RBAC", "Audit logs", "Custom retention", "Not sure"];
const TIMELINE = ["Now", "Within 30 days", "Within 90 days", "6+ months", "Just researching"];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

interface FormState {
  name: string;
  email: string;
  company: string;
  phone: string;
  companySize: string;
  users: string;
  jobs: string;
  systems: string[];
  security: string[];
  timeline: string;
  message: string;
  companyWebsite: string; // honeypot
}

const EMPTY_FORM: FormState = {
  name: "",
  email: "",
  company: "",
  phone: "",
  companySize: "",
  users: "",
  jobs: "",
  systems: [],
  security: [],
  timeline: "",
  message: "",
  companyWebsite: "",
};

type Errors = Partial<Record<keyof FormState, string>>;
type Status = "idle" | "submitting" | "success" | "error";

// Enterprise builds on the highest self-serve plan named in the catalog.
const includedPlanName = contactSalesPlan?.includesPlan ? planById(contactSalesPlan.includesPlan).name : "our self-serve plans";

const valueBullets = [
  "Contracted users, active jobs, and retention, with production installations defined in your quote",
  "FileAgent AI Assistant with your preferred supported Claude, Gemini, or OpenAI provider",
  "Advanced security options including SSO, LDAP, IP allowlisting, and session controls",
  "Job-scoped permissions for tighter operational control",
  "Support for complex transfer environments across local, SMB, SFTP, S3, and Azure Blob",
  "Support defined by your agreement, scoped deployment assistance, and custom commercial terms",
];

export function ContactSalesPage({ navigate }: { navigate: Navigate }) {
  const formStarted = useRef(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<Status>("idle");

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const toggleMulti = (key: "systems" | "security", option: string) => {
    setForm((prev) => {
      const set = new Set(prev[key]);
      set.has(option) ? set.delete(option) : set.add(option);
      return { ...prev, [key]: Array.from(set) };
    });
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = (): Errors => {
    const next: Errors = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.email.trim()) next.email = "Please enter your work email.";
    else if (!EMAIL_RE.test(form.email.trim())) next.email = "Enter a valid email address.";
    if (!form.company.trim()) next.company = "Please enter your company.";
    if (!form.companySize) next.companySize = "Select a company size.";
    if (!form.users) next.users = "Select estimated users.";
    if (!form.jobs) next.jobs = "Select estimated jobs.";
    if (form.systems.length === 0) next.systems = "Select at least one system.";
    if (form.security.length === 0) next.security = "Select at least one security need.";
    if (!form.timeline) next.timeline = "Select a timeline.";
    if (!form.message.trim()) next.message = "Tell us what you're trying to accomplish.";
    return next;
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (status === "submitting") return;
    const found = validate();
    if (Object.keys(found).length > 0) {
      track("contact_form_validation_failed", { field_count: Object.keys(found).length });
      setErrors(found);
      const firstKey = Object.keys(found)[0];
      document.getElementById(`field-${firstKey}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    track("contact_form_submitted");
    setStatus("submitting");
    const payload: ContactSalesPayload = {
      name: form.name.trim(),
      email: form.email.trim(),
      company: form.company.trim(),
      companySize: form.companySize,
      users: form.users,
      jobs: form.jobs,
      systems: form.systems,
      security: form.security,
      timeline: form.timeline,
      message: form.message.trim(),
      phone: form.phone.trim() || undefined,
      companyWebsite: form.companyWebsite, // honeypot
    };
    const result = await submitContactSales(payload);
    track(result.ok ? "contact_form_succeeded" : "contact_form_failed");
    setStatus(result.ok ? "success" : "error");
    if (result.ok) window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <section className="page-section contact-page">
      <div className="shell contact-grid">
        <aside className="contact-aside">
          <p className="eyebrow eyebrow-pill">
            <Building2 size={15} /> FileAgent Enterprise
          </p>
          <h2>Built for the transfers your business can't afford to miss</h2>
          <p className="lead">
            FileAgent Enterprise is for organizations that need contracted capacity, agreed retention, custom commercial
            terms, and a support agreement to match — with the advanced security, job-scoped permissions, and
            customer-controlled AI-assisted administration of {includedPlanName}. Enterprise agreements are quoted and invoiced.
          </p>
          <ul className="contact-bullets">
            {valueBullets.map((bullet) => (
              <li key={bullet}>
                <span className="tick">
                  <Check size={15} />
                </span>
                {bullet}
              </li>
            ))}
          </ul>
          <p className="contact-enterprise-note">
            <BadgeCheck size={16} /> This form is for Enterprise inquiries. For standard plans, see{" "}
            <a href="/pricing" onClick={navigate("/pricing")}>
              pricing
            </a>
            .
          </p>
        </aside>

        <div className="contact-form-card">
          {status === "success" ? (
            <div className="contact-success" role="status" aria-live="polite">
              <span className="contact-success-icon">
                <CheckCircle2 size={30} />
              </span>
              <h1>Thanks — we received your Enterprise request.</h1>
              <p>We'll review your details and follow up shortly.</p>
              <div className="contact-success-actions">
                <a className="button button-secondary" href="/pricing" onClick={navigate("/pricing")}>
                  Back to pricing <ArrowRight size={16} />
                </a>
                <a className="button button-secondary" href="/" onClick={navigate("/")}>
                  Return home
                </a>
              </div>
            </div>
          ) : (
            <>
              <div className="contact-form-head">
                <h1>Request Enterprise Pricing</h1>
                <p>Tell us a little about your environment so we can recommend the right FileAgent Enterprise plan.</p>
              </div>

              {status === "error" && (
                <div className="form-error-banner" role="alert">
                  <ShieldCheck size={18} />
                  <span>
                    Something went wrong while sending your request. Please try again or email{" "}
                    <a href={`mailto:${siteConfig.supportEmail}`}>{siteConfig.supportEmail}</a>.
                  </span>
                </div>
              )}

              <form className="contact-form" onSubmit={onSubmit} onFocus={() => { if (!formStarted.current) { formStarted.current = true; track("contact_form_started"); } }} noValidate>
                {/* Honeypot — visually hidden, ignored by humans. */}
                <div className="hp-field" aria-hidden="true">
                  <label htmlFor="field-companyWebsite">Company website</label>
                  <input
                    id="field-companyWebsite"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.companyWebsite}
                    onChange={(e) => update("companyWebsite", e.target.value)}
                  />
                </div>

                <div className="field-row">
                  <Field id="field-name" label="Name" required error={errors.name}>
                    <input
                      id="field-name"
                      type="text"
                      autoComplete="name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      aria-invalid={!!errors.name}
                    />
                  </Field>
                  <Field id="field-email" label="Work email" required error={errors.email}>
                    <input
                      id="field-email"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      aria-invalid={!!errors.email}
                    />
                  </Field>
                </div>

                <div className="field-row">
                  <Field id="field-company" label="Company" required error={errors.company}>
                    <input
                      id="field-company"
                      type="text"
                      autoComplete="organization"
                      value={form.company}
                      onChange={(e) => update("company", e.target.value)}
                      aria-invalid={!!errors.company}
                    />
                  </Field>
                  <Field id="field-phone" label="Phone number" hint="Optional">
                    <input
                      id="field-phone"
                      type="tel"
                      autoComplete="tel"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                    />
                  </Field>
                </div>

                <div className="field-row">
                  <SelectField
                    id="field-companySize"
                    label="Company size"
                    required
                    options={COMPANY_SIZE}
                    value={form.companySize}
                    error={errors.companySize}
                    onChange={(v) => update("companySize", v)}
                  />
                  <SelectField
                    id="field-users"
                    label="Estimated users"
                    required
                    options={USERS}
                    value={form.users}
                    error={errors.users}
                    onChange={(v) => update("users", v)}
                  />
                </div>

                <div className="field-row">
                  <SelectField
                    id="field-jobs"
                    label="Estimated jobs / workflows"
                    required
                    options={JOBS}
                    value={form.jobs}
                    error={errors.jobs}
                    onChange={(v) => update("jobs", v)}
                  />
                  <SelectField
                    id="field-timeline"
                    label="Timeline"
                    required
                    options={TIMELINE}
                    value={form.timeline}
                    error={errors.timeline}
                    onChange={(v) => update("timeline", v)}
                  />
                </div>

                <CheckboxGroup
                  id="field-systems"
                  label="Systems you need to connect"
                  required
                  options={SYSTEMS}
                  selected={form.systems}
                  error={errors.systems}
                  onToggle={(opt) => toggleMulti("systems", opt)}
                />

                <CheckboxGroup
                  id="field-security"
                  label="Security needs"
                  required
                  options={SECURITY}
                  selected={form.security}
                  error={errors.security}
                  onToggle={(opt) => toggleMulti("security", opt)}
                />

                <Field id="field-message" label="What are you trying to accomplish?" required error={errors.message}>
                  <textarea
                    id="field-message"
                    rows={4}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="Tell us about the file transfer, sync, migration, or automation workflow you need help with."
                    aria-invalid={!!errors.message}
                  />
                </Field>

                <button className="button button-primary button-lg contact-submit" type="submit" disabled={status === "submitting"}>
                  {status === "submitting" ? (
                    <>
                      <span className="spinner" aria-hidden="true" /> Sending…
                    </>
                  ) : (
                    <>
                      Request Pricing <ArrowRight size={18} />
                    </>
                  )}
                </button>
                <p className="contact-disclaimer">
                  <Mail size={14} /> We'll only use your details to respond to this request.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({
  id,
  label,
  required,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`field ${error ? "has-error" : ""}`}>
      <label htmlFor={id}>
        {label}
        {required ? <span className="req">*</span> : hint ? <span className="opt">{hint}</span> : null}
      </label>
      {children}
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}

function SelectField({
  id,
  label,
  required,
  options,
  value,
  error,
  onChange,
}: {
  id: string;
  label: string;
  required?: boolean;
  options: string[];
  value: string;
  error?: string;
  onChange: (value: string) => void;
}) {
  return (
    <Field id={id} label={label} required={required} error={error}>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={!!error} className={value ? "" : "placeholder"}>
        <option value="" disabled>
          Select…
        </option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </Field>
  );
}

function CheckboxGroup({
  id,
  label,
  required,
  options,
  selected,
  error,
  onToggle,
}: {
  id: string;
  label: string;
  required?: boolean;
  options: string[];
  selected: string[];
  error?: string;
  onToggle: (option: string) => void;
}) {
  return (
    <div className={`field ${error ? "has-error" : ""}`} id={id}>
      <span className="field-group-label">
        {label}
        {required && <span className="req">*</span>}
      </span>
      <div className="checkbox-group" role="group" aria-label={label}>
        {options.map((opt) => {
          const checked = selected.includes(opt);
          return (
            <label key={opt} className={`checkbox-pill ${checked ? "checked" : ""}`}>
              <input type="checkbox" checked={checked} onChange={() => onToggle(opt)} />
              <span className="checkbox-box" aria-hidden="true">
                {checked && <Check size={13} />}
              </span>
              {opt}
            </label>
          );
        })}
      </div>
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}
